/**
 * care-program.js
 *
 * Shared logic for the SafeOwl Care Program add-on.
 *
 * The Care Program is a digital product, sold either as coverage attached to a
 * device or on its own from its product page. An attached care line item is
 * bound to the device it covers by two line item properties:
 *
 *   Covers              — human-readable device name, e.g. "OWLY 1 — Black".
 *                         Visible to the customer in the cart, at checkout and
 *                         on the order.
 *   _care_for_variant   — the covered device's variant ID, as a string. The
 *                         leading underscore keeps it out of customer-facing
 *                         display while leaving it readable in /cart.js and in
 *                         the Shopify admin.
 *
 * Because those properties differ per device, Shopify keeps a separate care line
 * per device instead of merging them — which is exactly what we want.
 *
 * A standalone care line (bought from the Care Program's own product page) has
 * no `_care_for_variant`. It is treated as an ordinary product: rendered with
 * its own quantity stepper, and never removed or re-synced by `reconcile`.
 *
 * This module owns every decision about care so that side-cart.js, product.js
 * and cart.liquid share one implementation. It is deliberately render-agnostic:
 * it indexes the cart and builds payloads, but the side cart still owns how a
 * line item looks.
 *
 * Loaded before side-cart.js (see snippets/footer-scripts.liquid) so that
 * window.SafeOwlCare exists by the time the drawer initialises.
 *
 * Public API (window.SafeOwlCare):
 *   enabled                          — false when the feature is off or unconfigured
 *   config                           — { productId, variantId, priceFormatted, ... }
 *   isEligible(productId)            — does this product offer the add-on?
 *   markEligible(productId)          — register a product as eligible at runtime
 *   isCareLine(item)                 — is this cart line the Care Program?
 *   isAttachedCareLine(item)         — ...and bound to a device (not standalone)?
 *   parentVariantOf(careItem)        — variant ID the care line covers
 *   coversLabelOf(careItem)          — the "Covers" display string
 *   indexCart(cart)                  — group cart lines into display rows
 *   buildItems(...)                  — /cart/add.js payload for device + care
 *   addCareFor(...)                  — add coverage for a device already in cart
 *   reconcile(cart)                  — remove orphans, sync quantities (attached lines only)
 *   upsellRowHtml(item)              — the drawer's "Add SafeOwl Care" row
 *   deviceLabelForVariant(...)       — build a "Covers" label from a variant
 *   escapeHtml(value)                — attribute/text escaping for the above
 *   purchaseDateError(...)           — why a standalone watch purchase date is not accepted
 */
(function () {

    // Bumped whenever this file changes. window.SafeOwlCare.diagnose() prints it,
    // so there is a way to tell a stale deployed asset from a live one — "my new
    // logging produced no output" otherwise has two very different explanations.
    const BUILD = '2026-10-16d';

    // ── Line item property names ─────────────────────────────────────────────
    const COVERS_PROPERTY = 'Covers';
    const PARENT_PROPERTY = '_care_for_variant';


    // ── Configuration ────────────────────────────────────────────────────────
    // Emitted by snippets/care-program-config.liquid, which renders it whenever
    // the feature is switched on — including when the Shopify-side setup is
    // incomplete, in which case `status` says which part is wrong.
    const configEl = document.querySelector('[data-care-program-config]');

    let config = null;
    let status = 'off';

    if (configEl) {
        try {
            config = JSON.parse(configEl.textContent);
            status = config.status || 'ok';
        } catch {
            // Malformed JSON — refuse to enable rather than letting a parse error
            // take down the side cart.
            config = null;
            status = 'bad_config';
        }
    }

    const enabled = status === 'ok' && Boolean(config && config.variantId);

    const REASONS = {
        no_product: 'no product is selected in Theme settings → SafeOwl Care Program.',
        no_variant: 'the selected Care Program product has no variants.',
        unavailable: 'the Care Program variant is not available for sale. Check that the product is published to the Online Store sales channel, and that inventory tracking is off — it is a digital product, so it should not be stock-managed.',
        bad_config: 'the configuration block was found but could not be parsed as JSON.',
        off: 'the configuration block is not on the page at all. Either care_program_enabled is off in Theme settings, or snippets/care-program-config.liquid is not being reached — it is rendered from the end of snippets/side-cart.liquid.'
    };

    const describe = () => '[SafeOwl Care] The add-on is inactive: ' +
        (REASONS[status] || 'unrecognised status "' + status + '".');

    // Liquid and JavaScript decide independently whether coverage is on offer:
    // Liquid renders the checkbox and the upsell row from metafields and theme
    // settings, while this module builds the actual cart request from the config
    // block. When the two disagree the shopper gets a checkbox that ticks and
    // then quietly does nothing — the worst kind of failure, because it looks
    // like it worked and nothing anywhere says otherwise.
    //
    // So: if Liquid has put an add-on control on this page and we cannot honour
    // it, that is an error, not a warning. Note this is the one case worth
    // reporting even when the status is "off", because Liquid offering coverage
    // is proof the feature is not deliberately switched off.
    if (!enabled) {
        const liquidOffer = document.querySelector('[data-care-addon], [data-care-add]');

        if (liquidOffer) {
            console.error(
                describe() +
                ' Liquid has rendered an add-on control on this page, so coverage will be silently dropped if a shopper uses it.'
            );
        } else if (status !== 'off') {
            console.warn(describe());
        }
    }


    // ── Eligibility ──────────────────────────────────────────────────────────
    // Seeded from the products currently in the cart (resolved in Liquid, where
    // metafields are readable) and merged with any IDs registered by the product
    // page for the product being viewed. IDs are normalised to strings because
    // Liquid's `split` yields strings while /cart.js yields numbers.
    const eligibleIds = new Set();

    const markEligible = (productId) => {
        if (productId !== null && productId !== undefined && productId !== '') {
            eligibleIds.add(String(productId));
        }
    };

    if (config && Array.isArray(config.eligible)) {
        config.eligible.forEach(markEligible);
    }

    if (Array.isArray(window.SafeOwlCareEligible)) {
        window.SafeOwlCareEligible.forEach(markEligible);
    }

    const isEligible = (productId) => enabled && eligibleIds.has(String(productId));


    // ── Cart line helpers ────────────────────────────────────────────────────

    /**
     * Is this cart line the Care Program product?
     *
     * @param {Object} item — line item from /cart.js
     * @returns {boolean}
     */
    const isCareLine = (item) => Boolean(
        enabled && item && String(item.product_id) === String(config.productId)
    );

    /**
     * The variant ID a care line covers, or null if the property is missing
     * (the Care Program was bought on its own rather than as an add-on).
     *
     * @param {Object} careItem
     * @returns {string|null}
     */
    const parentVariantOf = (careItem) => {
        const properties = careItem && careItem.properties;
        const value = properties ? properties[PARENT_PROPERTY] : null;
        return value ? String(value) : null;
    };

    /**
     * Is this cart line Care Program coverage attached to a device? False for a
     * standalone purchase, which is handled like any other product.
     *
     * @param {Object} item — line item from /cart.js
     * @returns {boolean}
     */
    const isAttachedCareLine = (item) => isCareLine(item) && parentVariantOf(item) !== null;

    /**
     * The customer-facing "Covers" label on a care line, or an empty string.
     *
     * @param {Object} careItem
     * @returns {string}
     */
    const coversLabelOf = (careItem) => {
        const properties = careItem && careItem.properties;
        return (properties && properties[COVERS_PROPERTY]) || '';
    };

    /**
     * Builds the "Covers" label for a device.
     * Shopify reports single-variant products as "Default Title", which is noise
     * to a customer, so it is omitted.
     *
     * @param {string} productTitle
     * @param {string} [variantTitle]
     * @returns {string}
     */
    const deviceLabel = (productTitle, variantTitle) => (
        variantTitle && variantTitle !== 'Default Title'
            ? productTitle + ' — ' + variantTitle
            : productTitle
    );

    /**
     * Builds the "Covers" label from a variant ID by looking it up in
     * window.productVariants (dumped by sections/product.liquid). Used on the
     * product page, where the shopper can change variant after page load.
     *
     * @param {number|string} variantId
     * @param {string} productTitle
     * @returns {string}
     */
    const deviceLabelForVariant = (variantId, productTitle) => {
        const variants = Array.isArray(window.productVariants) ? window.productVariants : [];
        const variant = variants.find((v) => String(v.id) === String(variantId));
        return deviceLabel(productTitle, variant && variant.title);
    };


    // ── Cart indexing ────────────────────────────────────────────────────────

    /**
     * Groups a cart into the rows the side cart renders, in display order:
     * each device line, followed by its care line (if any).
     *
     * Care lines are matched to devices by variant ID. Device lines normally
     * carry no line item properties, so Shopify merges repeat adds of a variant
     * and a variant ID uniquely identifies a device line.
     *
     * Pre-order devices are the exception: they carry their ship date as a
     * property (snippets/pre-order-notice.liquid). Every add path sends the same
     * value, so their lines still merge — unless the ship date is changed while
     * a shopper already has the old line in their cart. The next add then lands
     * on a second line for the same variant. Coverage binds to the first line,
     * and the second shows the upsell row. Billing stays correct either way:
     * `reconcile` sums device quantity across every line of a variant, and
     * accepting the upsell merges into the existing care line (same Covers and
     * parent properties) rather than adding a second one.
     *
     * Orphans — care lines whose device is no longer in the cart — are returned
     * separately. `reconcile` normally removes them before a render happens; they
     * are surfaced here so that a failed reconcile shows the line rather than
     * silently hiding something the customer is still being charged for.
     *
     * Standalone care lines are rows of their own, like any device, but never
     * offer the upsell — coverage cannot cover coverage.
     *
     * @param {Object} cart — cart object from /cart.js
     * @returns {{rows: Array<{item: Object, care: (Object|null), showUpsell: boolean}>, orphans: Array<Object>}}
     */
    const indexCart = (cart) => {
        const items = (cart && cart.items) || [];

        if (!enabled) {
            return {
                rows: items.map((item) => ({ item, care: null, showUpsell: false })),
                orphans: []
            };
        }

        const devices = [];
        const careLines = [];

        items.forEach((item) => {
            if (isAttachedCareLine(item)) {
                careLines.push(item);
            } else {
                devices.push(item);
            }
        });

        // Bind each care line to the first device line matching its parent variant.
        const careByDeviceKey = new Map();
        const orphans = [];

        careLines.forEach((careItem) => {
            const parentVariant = parentVariantOf(careItem);
            const device = devices.find((d) => String(d.variant_id) === parentVariant && !careByDeviceKey.has(d.key));

            if (device) {
                careByDeviceKey.set(device.key, careItem);
            } else {
                orphans.push(careItem);
            }
        });

        const rows = devices.map((item) => {
            const care = careByDeviceKey.get(item.key) || null;
            return {
                item,
                care,
                showUpsell: !care && !isCareLine(item) && isEligible(item.product_id)
            };
        });

        return { rows, orphans };
    };


    // ── Cart mutation ────────────────────────────────────────────────────────

    /**
     * Builds the /cart/add.js `items` payload for a device plus its coverage.
     * Used when the add-on checkbox is ticked on the product page, so both lines
     * are added in a single request.
     *
     * @param {number|string} deviceVariantId
     * @param {number} quantity
     * @param {string} coversLabel        — value for the care line's "Covers" property
     * @param {Object} [deviceProperties] — line item properties for the device itself
     * @returns {Array<Object>}
     */
    const buildItems = (deviceVariantId, quantity, coversLabel, deviceProperties) => {
        const device = { id: Number(deviceVariantId), quantity: quantity };

        if (deviceProperties && Object.keys(deviceProperties).length > 0) {
            device.properties = deviceProperties;
        }

        if (!enabled) {
            // Reaching here means a shopper ticked the box and the device is
            // about to be added without the coverage they asked for. Falling
            // back to the device alone is the right behaviour — better than
            // failing the whole add — but doing it quietly is not.
            console.error(describe() + ' Coverage was requested but has been dropped; only the product was added.');
            return [device];
        }

        const care = { id: Number(config.variantId), quantity: quantity, properties: {} };
        care.properties[COVERS_PROPERTY] = coversLabel;
        care.properties[PARENT_PROPERTY] = String(deviceVariantId);

        return [device, care];
    };

    /**
     * POSTs items to the Shopify Cart API.
     *
     * @param {Array<Object>} items
     * @returns {Promise<Object>}
     */
    const addItems = (items) => fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
    }).then((response) => {
        if (!response.ok) {
            return response.json().then((data) => {
                throw new Error(data.description || 'Could not add to cart.');
            });
        }
        return response.json();
    });

    /**
     * Adds coverage for a device already in the cart (the side cart upsell row).
     *
     * @param {number|string} deviceVariantId
     * @param {number} quantity     — matched to the device's current quantity
     * @param {string} coversLabel
     * @returns {Promise<Object>}
     */
    const addCareFor = (deviceVariantId, quantity, coversLabel) => {
        if (!enabled) {
            console.error(describe() + ' Coverage could not be added from the cart.');
            return Promise.reject(new Error('SafeOwl Care add-on is inactive'));
        }

        const care = { id: Number(config.variantId), quantity: quantity, properties: {} };
        care.properties[COVERS_PROPERTY] = coversLabel;
        care.properties[PARENT_PROPERTY] = String(deviceVariantId);

        return addItems([care]);
    };

    /**
     * Changes a line's quantity by line item key.
     *
     * @param {string} key
     * @param {number} quantity
     * @returns {Promise<Object>}
     */
    const changeLine = (key, quantity) => fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
    }).then((response) => {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
    });


    // ── Reconciliation ───────────────────────────────────────────────────────
    // Guards against re-entry: reconcile mutates the cart, and every cart
    // mutation triggers a re-render, which would otherwise call reconcile again.
    let isReconciling = false;

    /**
     * Enforces the two rules that make coverage "auto-managed":
     *
     *   1. A care line whose device has left the cart is removed.
     *   2. A care line's quantity is kept equal to its device's quantity.
     *
     * Both apply to attached care lines only. A standalone Care Program — bought
     * from its own product page, so it has no parent property — is a purchase in
     * its own right and is never touched.
     *
     * Changes are applied sequentially rather than in parallel — concurrent
     * writes to the same cart can race on Shopify's side.
     *
     * @param {Object} cart — cart object from /cart.js
     * @returns {Promise<boolean>} true if the cart was modified, meaning the
     *          caller should re-fetch rather than render the cart it passed in
     */
    const reconcile = async (cart) => {
        if (!enabled || isReconciling || !cart || !cart.items) return false;

        const careLines = cart.items.filter(isAttachedCareLine);
        if (careLines.length === 0) return false;

        // Total device quantity per variant. Summed rather than assigned, so a
        // variant split across multiple lines still resolves to one number.
        const deviceQtyByVariant = new Map();

        cart.items.forEach((item) => {
            if (isCareLine(item)) return;
            const key = String(item.variant_id);
            deviceQtyByVariant.set(key, (deviceQtyByVariant.get(key) || 0) + item.quantity);
        });

        const changes = [];

        careLines.forEach((careItem) => {
            const deviceQty = deviceQtyByVariant.get(parentVariantOf(careItem));

            if (!deviceQty) {
                // Orphaned: its device has left the cart — drop it.
                changes.push({ key: careItem.key, quantity: 0 });
            } else if (careItem.quantity !== deviceQty) {
                changes.push({ key: careItem.key, quantity: deviceQty });
            }
        });

        if (changes.length === 0) return false;

        isReconciling = true;

        try {
            for (const change of changes) {
                await changeLine(change.key, change.quantity);
            }
            return true;
        } catch {
            // Leave the cart as-is; the next reconcile pass will retry. Returning
            // false means the caller renders what it already has rather than
            // firing a pointless re-fetch.
            return false;
        } finally {
            isReconciling = false;
        }
    };


    // ── Rendering helpers ────────────────────────────────────────────────────

    /**
     * Escapes a value for safe interpolation into HTML text or a quoted attribute.
     *
     * @param {*} value
     * @returns {string}
     */
    const escapeHtml = (value) => String(value === null || value === undefined ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    /**
     * The side cart's upsell row, rendered beneath an eligible device line that
     * has no coverage yet.
     *
     * The button carries everything the click handler needs, because the drawer
     * replaces its own innerHTML on every render and the handler is delegated
     * from the drawer root. It renders aria-disabled until the terms checkbox
     * is ticked.
     *
     * Mirrors snippets/side-cart-care-upsell.liquid.
     *
     * @param {Object} item — the device line item
     * @returns {string} HTML
     */
    const upsellRowHtml = (item) => {
        if (!enabled) return '';

        const label = deviceLabel(item.product_title, item.variant_title);

        const learnMore = config.hasLearnMore
            ? '<button type="button" class="side-cart__care-learn-more" data-care-more-open aria-haspopup="dialog">Learn more</button>'
            : '';

        return `
            <div class="side-cart__care-upsell">
                <div class="side-cart__care-upsell-info">
                    <div class="side-cart__care-upsell-heading">
                        <span class="side-cart__care-upsell-label">${escapeHtml(config.label)}</span>
                        ${learnMore}
                    </div>
                    <div class="side-cart__care-upsell-terms">
                        <label>
                            <input type="checkbox" name="care_terms" data-care-terms required>
                            I agree to the SafeOwl Care <a href="https://safeowl.com/pages/safeowl-care-terms-and-conditions" target="_blank" rel="noopener noreferrer">terms and conditions</a>
                        </label>
                    </div>
                    <div class="side-cart__care-upsell-footer">
                        <span class="side-cart__care-upsell-price">${escapeHtml(config.priceFormatted)}</span>
                        <button
                            type="button"
                            class="side-cart__care-add"
                            data-care-add
                            data-variant="${escapeHtml(item.variant_id)}"
                            data-quantity="${escapeHtml(item.quantity)}"
                            data-covers="${escapeHtml(label)}"
                            aria-disabled="true"
                        >Add</button>
                    </div>
                </div>
            </div>
        `;
    };


    // ── "Learn more" popup ───────────────────────────────────────────────────
    // One <dialog> per page, rendered globally by care-program-dialog.liquid, so
    // the product page trigger and the side cart trigger open the same thing.
    //
    // Delegated from the document rather than bound to the triggers: the side
    // cart replaces its markup on every refresh, so its trigger does not exist
    // yet when this module initialises and would not survive a re-render.
    //
    // showModal() provides focus trapping, Escape-to-close and an inert
    // background natively, and renders in the top layer — above the side cart
    // without any z-index coordination.

    const learnMoreDialog = () => document.querySelector('[data-care-more-dialog]');

    const openLearnMore = () => {
        const dialog = learnMoreDialog();
        if (!dialog) return;

        if (typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else {
            // No <dialog> support: still reveal the content rather than leaving
            // the trigger doing nothing.
            dialog.setAttribute('open', 'open');
        }
    };

    const closeLearnMore = () => {
        const dialog = learnMoreDialog();
        if (!dialog) return;

        if (typeof dialog.close === 'function') {
            dialog.close();
        } else {
            dialog.removeAttribute('open');
        }
    };

    document.addEventListener('click', (event) => {
        if (event.target.closest('[data-care-more-open]')) {
            event.preventDefault();
            openLearnMore();
            return;
        }

        if (event.target.closest('[data-care-more-close]')) {
            event.preventDefault();
            closeLearnMore();
            return;
        }

        // A modal <dialog> fills the viewport, so a click landing on the element
        // itself rather than on the inner panel is a backdrop click.
        const dialog = learnMoreDialog();
        if (dialog && event.target === dialog) closeLearnMore();
    });


    // ── Standalone purchase fields ───────────────────────────────────────────
    // Bought on its own, the Care Program asks when the watch was bought
    // (snippets/care-program-purchase-fields.liquid). Coverage only applies
    // within a fixed window of that date, so a date outside it is made invalid
    // and the browser's own form validation blocks the add.
    //
    // Runs whether or not the add-on is enabled: these fields belong to the
    // Care Program's own product page, not to the add-on.
    //
    // The window is worked out here from the shopper's local date rather than
    // in Liquid, where 'now' is frozen into the cached page and uses the store's
    // timezone.
    //
    // This is a storefront check. It stops a normal shopper, but a hand-built
    // /cart/add.js request bypasses it.

    /**
     * Formats a Date as a local YYYY-MM-DD string — the format an
     * <input type="date"> uses for its value, min and max.
     *
     * @param {Date} date
     * @returns {string}
     */
    const toIsoDate = (date) => [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0')
    ].join('-');

    /**
     * Shifts a YYYY-MM-DD date by a number of days. Built from local date
     * parts, so the Date constructor handles month and year rollover.
     *
     * @param {string} iso
     * @param {number} days — negative to go back
     * @returns {string}
     */
    const shiftIsoDate = (iso, days) => {
        const [year, month, day] = iso.split('-').map(Number);
        return toIsoDate(new Date(year, month - 1, day + days));
    };

    /**
     * Reorders a YYYY-MM-DD date as MM/DD/YYYY, the US-style date that is
     * saved to the cart line and shown on the order.
     *
     * @param {string} iso
     * @returns {string}
     */
    const toUsDate = (iso) => {
        const [year, month, day] = iso.split('-');
        return month + '/' + day + '/' + year;
    };

    /**
     * Why a watch purchase date is not accepted, or '' when it is. An empty
     * value also returns '': `required` on the input reports that case.
     * YYYY-MM-DD strings sort correctly as plain strings, so no parsing is
     * needed to compare them.
     *
     * @param {string} value      — the input's value, YYYY-MM-DD or ''
     * @param {string} todayIso   — today's local date, YYYY-MM-DD
     * @param {number} windowDays — days after the watch purchase that coverage can still be bought
     * @returns {string}
     */
    const purchaseDateError = (value, todayIso, windowDays) => {
        if (!value) return '';

        if (value > todayIso) {
            return 'Enter the date you bought your watch. It can\'t be in the future.';
        }

        if (value < shiftIsoDate(todayIso, -windowDays)) {
            return 'SafeOwl Care must be bought within ' + windowDays + ' days of your watch purchase.';
        }

        return '';
    };

    /**
     * Swaps the browser's date picker for a Flatpickr calendar, on computers
     * only. Phones and tablets (a coarse pointer) keep their own picker, which
     * is already good. Flatpickr is loaded by the snippet on the Care Program
     * page alone; anywhere it is missing, the browser's picker simply stays.
     *
     * Flatpickr keeps `input` as the real field — hidden, holding YYYY-MM-DD,
     * still carrying the property name — and shows a text field beside it in
     * MM/DD/YYYY. It fires `input` and `change` on `input` whenever the date
     * changes, so the window check and the formdata rewrite work unchanged.
     *
     * Deliberately not given minDate/maxDate: Flatpickr silently empties a
     * typed date outside them, so the shopper would lose what they typed and
     * never see why. Out-of-window days are greyed out in onDayCreate instead —
     * Flatpickr refuses to select a day with that class — and a typed date is
     * kept for purchaseDateError() to explain.
     *
     * `allowInput` matters for more than typing: without it Flatpickr makes the
     * field read-only, and the browser skips read-only fields when validating,
     * so neither `required` nor the window check would block an add.
     *
     * @param {HTMLInputElement} input
     * @param {number} windowDays
     * @returns {HTMLInputElement|null} the field Flatpickr shows, or null when
     *          the browser's picker stays
     */
    const attachCalendar = (input, windowDays) => {
        if (typeof window.flatpickr !== 'function') return null;
        if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return null;

        // Copied onto the visible field by Flatpickr.
        input.placeholder = 'MM/DD/YYYY';

        const picker = window.flatpickr(input, {
            dateFormat: 'Y-m-d',
            altInput: true,
            altFormat: 'm/d/Y',
            altInputClass: input.className,
            allowInput: true,
            // The phone-or-computer decision is made above; stop Flatpickr
            // second-guessing it from the user agent.
            disableMobile: true,
            onDayCreate: (selectedDates, dateStr, instance, dayElem) => {
                if (purchaseDateError(toIsoDate(dayElem.dateObj), toIsoDate(new Date()), windowDays)) {
                    dayElem.classList.add('flatpickr-disabled');
                    dayElem.setAttribute('aria-disabled', 'true');
                }
            }
        });

        const field = picker.altInput;

        // The label's `for` and the help text's aria-describedby both work by
        // id, so they move to the field the shopper actually uses.
        field.id = input.id;
        input.removeAttribute('id');

        const describedBy = input.getAttribute('aria-describedby');
        if (describedBy) field.setAttribute('aria-describedby', describedBy);

        field.autocomplete = 'off';

        return field;
    };

    document.querySelectorAll('[data-care-purchase-date]').forEach((input) => {
        const windowDays = parseInt(input.dataset.windowDays, 10) || 30;

        // Where the browser's validation happens: the input itself, or
        // Flatpickr's visible field once the calendar is attached (the input
        // is then hidden, and hidden fields are never validated).
        const field = attachCalendar(input, windowDays) || input;

        // Recomputes "today" on every call, so a page left open past midnight
        // still checks against the right window.
        const validate = () => {
            const today = toIsoDate(new Date());
            input.max = today;
            input.min = shiftIsoDate(today, -windowDays);
            field.setCustomValidity(purchaseDateError(input.value, today, windowDays));
        };

        // Once on load too, for a value the browser restored from history.
        validate();
        input.addEventListener('input', validate);
        input.addEventListener('change', validate);

        // A date input always submits YYYY-MM-DD, whatever the shopper sees.
        // `formdata` fires whenever FormData is built from the form — the AJAX
        // add in side-cart.js, the sticky bar in product.js and a native submit
        // alike — so rewriting the entry here covers every path at once.
        // Without JavaScript the YYYY-MM-DD date is sent unchanged.
        if (input.form) {
            input.form.addEventListener('formdata', (event) => {
                if (input.value) event.formData.set(input.name, toUsDate(input.value));
            });
        }
    });


    /**
     * Prints everything needed to explain "I ticked the box and nothing happened".
     *
     * Run window.SafeOwlCare.diagnose() in the browser console on a product page.
     * Deliberately reports the DOM relationships as well as the config, because
     * the checkbox is only honoured when it sits INSIDE the product form — the
     * submit handler scopes its lookup to the form being submitted.
     *
     * @returns {Object} the same report that is logged
     */
    const diagnose = () => {
        const toggle = document.querySelector('[data-care-addon]');
        const form = toggle ? toggle.closest('form') : null;
        const productForms = document.querySelectorAll('form[action*="/cart/add"]');

        const report = {
            build: BUILD,
            status: status,
            enabled: enabled,
            configElementFound: Boolean(configEl),
            config: config,
            eligibleIds: Array.from(eligibleIds),
            windowRegistry: window.SafeOwlCareEligible || null,
            checkboxFound: Boolean(toggle),
            checkboxChecked: toggle ? toggle.checked : null,
            checkboxInsideAForm: Boolean(form),
            checkboxFormAction: form ? form.getAttribute('action') : null,
            productFormsOnPage: productForms.length,
            checkboxInsideProductForm: Boolean(
                form && Array.prototype.indexOf.call(productForms, form) !== -1
            ),
            upsellRowsOnPage: document.querySelectorAll('[data-care-add]').length
        };

        console.log('[SafeOwl Care] diagnostics', report);
        return report;
    };


    // ── Public API ───────────────────────────────────────────────────────────
    window.SafeOwlCare = {
        build: BUILD,
        diagnose: diagnose,
        enabled: enabled,
        // Exposed for debugging: `window.SafeOwlCare.status` in the console names
        // the misconfiguration when the add-on is not active.
        status: status,
        config: config,
        COVERS_PROPERTY: COVERS_PROPERTY,
        PARENT_PROPERTY: PARENT_PROPERTY,
        isEligible: isEligible,
        markEligible: markEligible,
        isCareLine: isCareLine,
        isAttachedCareLine: isAttachedCareLine,
        parentVariantOf: parentVariantOf,
        coversLabelOf: coversLabelOf,
        deviceLabel: deviceLabel,
        deviceLabelForVariant: deviceLabelForVariant,
        indexCart: indexCart,
        buildItems: buildItems,
        addItems: addItems,
        addCareFor: addCareFor,
        reconcile: reconcile,
        upsellRowHtml: upsellRowHtml,
        escapeHtml: escapeHtml,
        purchaseDateError: purchaseDateError,
        openLearnMore: openLearnMore,
        closeLearnMore: closeLearnMore
    };

})();
